'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import type { Restaurant } from '@/lib/types/customer/RestaurantSearch';
import { Cart, DeliveryMini, Location, Star, Time } from '@ui/Icons';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import If from '@ui/If';
import { storeFront } from '@/lib/utils/routeLinks';
import useAppLocale from '@/hooks/useAppLocale';

interface SearchCardProps {
  restaurant: Restaurant;
  type?: 'delivery' | 'pickup' | 'all';
}

const SearchCard = ({ restaurant, type }: SearchCardProps) => {
  const { lang } = useAppLocale();
  const T_COMMON = useTranslations('');
  const T_PROFILE = useTranslations('storefront.store_profile');
  const coverUrl = restaurant.media?.cover?.[0].thumb_url ?? '/heroBurger.png';
  const logoUrl = restaurant.media?.logo?.[0].thumb_url;
  const {
    slug,
    name,
    address,
    short_description,
    avg_total_rating: rating,
    restaurant_config: config,
  } = restaurant;

  const greyCardBg =
    restaurant?.features?.ui_storefront_use_gray_background_in_list ?? true;
  const restaurantAllowsDelivery =
    (restaurant.allows_delivery && restaurant?.features?.handle_orders) ||
    false;

  const { is_open: isOpened, is_opening_soon: isOpeningSoon } = restaurant;

  return (
    <div
      className={cn(
        'group flex w-full overflow-hidden rounded-[10px] border border-[#E5E7EB] bg-white transition-shadow duration-500 hover:shadow-[0px_20px_60px_rgba(0,0,0,0.15)]',
        greyCardBg && 'grayscale'
      )}
    >
      <Link
        href={`${storeFront?.restaurants[lang as 'de' | 'en']()}/` + slug ?? ''}
        aria-label={T_COMMON('aria.go_to_this_restaurant', {
          restaurant: name,
        })}
        className={cn('flex w-full flex-col')}
      >
        <div className="relative">
          <div
            className="h-[200px] max-h-[190px] overflow-hidden"
            style={{
              backgroundImage: `url(${coverUrl})`,
              objectFit: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
            }}
          ></div>

          {logoUrl && (
            <div className=" absolute bottom-[-21px] left-[25px] h-[56px] w-[56px] overflow-hidden rounded-[5px] border border-[#E5E7EB]">
              <Image
                src={logoUrl}
                alt={`${name} brand logo`}
                className="object-cover"
                fill
                sizes="100vw"
              />
            </div>
          )}
        </div>
        <div className="flex flex-grow flex-col rounded-b-[10px] px-[25PX] pb-[20px] pt-[35PX] transition-all duration-500 group-hover:border-transparent">
          <h2 className="truncate text-h4 font-[700] text-title">{name}</h2>
          {((type && type === 'pickup') || restaurant.type === 'online_shop') &&
            short_description && (
              <p className="mt-[10px] truncate text-sm text-paragraph">
                {short_description}
              </p>
            )}
          <If condition={restaurant.type !== 'online_shop'}>
            <div className="mt-[15px] flex items-center text-[#A5A5AA]">
              <div className="w-[23px]">
                <Location />
              </div>
              {!!address && (
                <span className="ml-[6px] block text-paragraph">
                  {address.street_name} {address.house_number},{' '}
                  {address.zip_code} {address.city}
                </span>
              )}
            </div>
          </If>

          <If condition={restaurantAllowsDelivery}>
            {type && type !== 'pickup' && (
              <>
                <div className="mt-[10px] flex items-center">
                  <If condition={config.minimum_order_value_for_delivery > 0}>
                    <div className="mr-[16px] flex items-center text-[#A5A5AA]">
                      <Cart />
                      <span className="ml-[6px] text-sm text-paragraph">
                        Min.{' '}
                        {
                          config.formatted_currency
                            .minimum_order_value_for_delivery
                        }
                      </span>
                    </div>
                  </If>

                  <div className="flex items-center text-[#A5A5AA]">
                    <DeliveryMini />
                    <span className="ml-[6px] text-sm text-paragraph">
                      {config.allows_free_delivery || config.delivery_fee === 0
                        ? T_COMMON('free')
                        : config.formatted_currency.delivery_fee}
                    </span>
                  </div>
                </div>

                <If
                  condition={
                    config.minimum_delivery_time_in_minutes > 0 &&
                    config.maximum_delivery_time_in_minutes > 0
                  }
                >
                  <div className="mt-[10px] flex items-center text-[#A5A5AA]">
                    <Time />
                    <span className="ml-[6px] block text-paragraph">
                      {config.minimum_delivery_time_in_minutes}-
                      {config.maximum_delivery_time_in_minutes} min
                    </span>
                  </div>
                </If>
              </>
            )}
          </If>
          <div className="mt-[24px] flex flex-grow items-end justify-between">
            {rating.counts.all > 0 && (
              <div className="flex items-center">
                <Star />
                <span className="mx-[8px] inline-block text-h6 font-[700] text-[#646464]">
                  {rating.formatted.all}
                </span>
                <span>({rating.counts.all})</span>
              </div>
            )}
            {/* {type && type === 'pickup' && restaurant.allows_delivery && (
              <span
                className={`ml-auto inline-block rounded-full border border-[rgba(0,127,95,0.30)] bg-[linear-gradient(0deg,_rgba(36,192,48,0.08)_0%,_rgba(36,192,48,0.08)_100%)] px-[10px] py-[6px] text-xs text-[#007F5F] ${
                  rating.counts.all > 0 ? '' : 'self-end'
                }`}
              >
                {T_SEARCH('delivery_available')}
              </span>
            )} */}
            <span
              className={cn(
                'ml-auto inline-block rounded-full border px-[10px] py-[6px] text-xs uppercase leading-3',
                rating.counts.all > 0 ? '' : 'self-end',
                isOpened &&
                  'border-[rgba(126,226,184,0.6)] bg-[#24C03014] text-[#007F5F]',
                isOpeningSoon &&
                  'border-[#f5cd47] bg-[#f5cd471a] text-[#f2a33a]',
                !isOpened &&
                  !isOpeningSoon &&
                  'border-[#ef000014] bg-[#ef000014] text-[#EF0000]'
              )}
            >
              {isOpened && T_PROFILE('opened')}
              {!isOpened && isOpeningSoon && T_PROFILE('opening_soon')}
              {!isOpened && !isOpeningSoon && T_PROFILE('currently_closed')}
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};
export default SearchCard;
